import { useTranslation } from 'react-i18next'

import { ASYNC_TASK_EXPORT_INTERVAL } from '../../../../constants/async'
import { EMPTY_OBJECT } from '../../../../constants/common'
import { useAsyncTask, useNotification, useUserData } from '../../../../hooks'
import {
  getAsyncTaskId,
  isAsyncTaskFinished,
  isAsyncTaskSuccessStatus,
} from '../../../../utils/async'
import { isDwellerRole } from '../../../../utils/user'
import {
  GET_RESPONSE_LINKS_TYPE_FIELD_NAME,
  GetResponseLinksTypeOptionValue,
} from '../TableExportGetResponseLinkTypeModal.constants'
import { getFileDirectoryLink } from '../TableExportGetResponseLinkTypeModal.utils'

export function useExportGetResponseLinkTypeFormSubmit({
  excludeIds,
  filterValues,
  getApiRequestFilterParams,
  massActionIdKey,
  selectAllPages,
  selectedIds,
  sub,
  unselectAllPages,
  onCheckTask,
  onClose,
  onSetFilesIds,
  onSetLoading,
  onSetWithSubmitButton,
  onStartExport,
}) {
  const { t } = useTranslation()

  const {
    handleAddErrorNotification,
    handleAddInfoNotification,
    handleAddSuccessNotification,
  } = useNotification()

  const { handleAddAsyncTask, handleRemoveAsyncTask } = useAsyncTask()

  const user = useUserData()

  const isDwellerUser = isDwellerRole(user || EMPTY_OBJECT)

  function submit(handler) {
    const { filters } = getApiRequestFilterParams({
      excludeIds,
      filterValues,
      massActionIdKey,
      selectAllPages,
      selectedIds,
      sub,
    })

    return onStartExport({
      filters,
    })
      .then(({ data }) => {
        const asyncTask = getAsyncTaskId(data)

        handleAddAsyncTask({
          id: asyncTask,
          interval: setInterval(() => {
            checkTaskStatus(asyncTask, handler)
          }, ASYNC_TASK_EXPORT_INTERVAL),
        })
      })
      .catch(() => {
        handleAddErrorNotification('Error.CommonApiRequestFailKey')
      })
  }

  function checkTaskStatus(task, handler) {
    onCheckTask(task)
      .then(({ data }) => {
        if (isAsyncTaskFinished(data)) {
          handler(data)
          handleRemoveAsyncTask(task)
          unselectAllPages?.()
        }
      })
      .catch(() => {
        handleRemoveAsyncTask(task)
        handleAddErrorNotification('Error.CommonApiRequestFailKey')
      })
  }

  function handleTaskFinish(data, onSuccess) {
    onSetLoading(false)

    if (isAsyncTaskSuccessStatus(data)) {
      onSuccess()
    } else {
      handleAddErrorNotification('Error.CommonApiRequestFailKey')
    }
  }

  function handleTaskFinishLater(data) {
    handleTaskFinish(data, () => {
      unselectAllPages?.()

      if (data.directory_id) {
        handleAddSuccessNotification({
          linkProps: {
            reloadDocument: true,
            title: t('Request.ExportFinishedNotificationTextKey'),
            to: getFileDirectoryLink({
              directoryId: data.directory_id,
              isDwellerUser,
            }),
          },
          title: t('Request.ModalExportFinishedKey'),
        })
      } else {
        handleAddErrorNotification({
          text: t('Error.ExportRequestFailedNoDataTextKey'),
          title: t('Error.ExportRequestsFailedTitleKey'),
        })
      }
    })
  }

  function handleTaskFinishWait(data) {
    handleTaskFinish(data, () => {
      onSetFilesIds(data.files)
    })
  }

  function handleSubmitGetResponseLinksLater() {
    onClose()
    unselectAllPages?.()
    handleAddInfoNotification('Notification.ExportHasStartedKey')

    submit(handleTaskFinishLater)
  }

  function handleSubmitGetResponseLinksWait() {
    onSetLoading(true)
    onSetWithSubmitButton(false)

    submit(handleTaskFinishWait)
  }

  function handleSubmit({ values }) {
    switch (values[GET_RESPONSE_LINKS_TYPE_FIELD_NAME]) {
      case GetResponseLinksTypeOptionValue.Later:
        handleSubmitGetResponseLinksLater()

        break

      case GetResponseLinksTypeOptionValue.Wait:
        handleSubmitGetResponseLinksWait()

        break
    }
  }

  return { handleSubmit }
}
